
<div :class="['profile-profile', { page }]">
  <spinner v-if="!loaded" />

  <template v-else>
    <section :class="['profile-header', { page, 'profile-header--no-image': !headerProfileImage }]">
      <div class="profile-header-actions">
        <align justify="space-between">
          <align small-gap>
            <icon-button
              v-if="!page"
              tooltip="Close profile"
              icon="close"
              @click="close"
            />

            <icon-button
              v-if="!page && profileId"
              tooltip="View full profile"
              class="medium-up"
              icon="fullscreen"
              @click="fullscreen"
            />
          </align>

          <dropdown
            v-if="isStaff && profileMenuOptions.length"
            align="right"
            :active="profileMenuOpen"
            @close="profileMenuOpen = false"
          >
            <icon-button
              icon="more_vert"
              @click="profileMenuOpen = !profileMenuOpen"
            />

            <template #menu>
              <dropdown-menu
                :options="profileMenuOptions"
                @select="handleProfileMenuSelect"
              />
            </template>
          </dropdown>
        </align>
      </div>

      <div v-if="headerProfileImage" class="profile-header-image">
        <card-profile-images :card="{ image: headerProfileImage }" :page="page" />
      </div>

      <div class="profile-header-info">
        <div class="profile-header-title">
          <h3>{{ profile.label }}</h3>

          <p class="profile-graders">
            <span
              v-for="graderItem in profile.graders"
              :key="graderItem.grader"
              :class="[
                'profile-grader-logo-pill',
                {
                  'profile-grader-logo-pill--interactive': profile.graders.length > 1,
                  'profile-grader-logo-pill--selected':
                    profile.graders.length > 1 && salesStripGrader === graderItem.grader,
                },
              ]"
              :role="profile.graders.length > 1 ? 'button' : undefined"
              :tabindex="profile.graders.length > 1 ? 0 : -1"
              :aria-pressed="profile.graders.length > 1 ? salesStripGrader === graderItem.grader : undefined"
              @click="onHeaderGraderClick(graderItem)"
              @keyup.enter="onHeaderGraderClick(graderItem)"
              @keyup.space.prevent="onHeaderGraderClick(graderItem)"
            >
              <img
                v-if="getGraderLogoUrl(graderItem.grader)"
                class="profile-grader-logo-img"
                :src="getGraderLogoUrl(graderItem.grader)"
                :alt="graderItem.label"
              />
              <pill v-else>
                {{ graderItem.label }}
              </pill>
            </span>

            <icon-button
              v-if="profile.excludeFromMatches === true"
              class="profile-excluded-indicator"
              :class="{ 'profile-excluded-indicator--staff-action': isStaff }"
              icon="visibility_off"
              :tooltip="excludeFromMatchesIndicatorTooltip"
              :loading="restoringToMatchQueue"
              @click="onExcludeFromMatchesIndicatorClick"
            />
          </p>
        </div>

        <div :class="['stats-grid', 'profile-header-stats', { page }]">
          <stat-item
            class="profile-stat"
            label="Total Population"
            :value="$filters.number(profile.totalPopulation)"
          />

          <stat-item
            class="profile-stat"
            label="Total Gems"
            :value="$filters.number(profile.totalGems)"
          />

          <stat-item
            class="profile-stat"
            label="Gem Rate"
            :value="$filters.percentage(profile.gemRate)"
          />
        </div>

        <div class="profile-attributes">
          <div class="profile-attribute">
            <span class="profile-attribute-label">Category</span>
            <span class="profile-attribute-value">{{ profile.category }}</span>
          </div>

          <div class="profile-attribute">
            <span class="profile-attribute-label">Year</span>
            <span class="profile-attribute-value">{{ profile.year }}</span>
          </div>

          <div class="profile-attribute">
            <span class="profile-attribute-label">Set</span>
            <span class="profile-attribute-value">{{ profile.set }}</span>
          </div>

          <div class="profile-attribute">
            <span class="profile-attribute-label">Number</span>
            <span class="profile-attribute-value">{{ profile.number }}</span>
          </div>

          <div class="profile-attribute">
            <span class="profile-attribute-label">Subject</span>
            <span class="profile-attribute-value">{{ profile.subject }}</span>
          </div>

          <div v-if="profile.variation" class="profile-attribute">
            <span class="profile-attribute-label">Variation</span>
            <span class="profile-attribute-value">{{ profile.variation }}</span>
          </div>
        </div>

        <div v-if="profileId" class="profile-view-all-sales-cta">
          <btn kind="secondary" block @click="goToSalesProfileOnly">
            View All Sales
          </btn>
        </div>
      </div>
    </section>

    <section
      v-if="showProfileSalesSection"
      ref="profileSalesSection"
      class="profile-sales-row"
    >
      <align justify="space-between">
        <h4 class="short-text secondary-font">
          Sales
        </h4>

        <icon-link @click="goToSales">
          View All Sales
        </icon-link>
      </align>

      <align
        v-if="profile && salesGraderDropdownOptions.length"
        class="profile-sales-filters"
        small-gap
      >
        <pill-dropdown
          :value="salesStripGrader"
          align="left"
          :options="salesGraderDropdownOptions"
          class="profile-sales-filter-dropdown"
          @input="onSalesGraderChange"
        />
        <pill-dropdown
          v-if="salesGradeDropdownOptions.length"
          :value="salesStripGrade"
          align="left"
          :options="salesGradeDropdownOptions"
          class="profile-sales-filter-dropdown"
          @input="onSalesGradeChange"
        />
        <pill-dropdown
          v-if="salesPopulationTypeDropdownOptions.length > 1"
          :value="salesStripPopulationType"
          align="left"
          :options="salesPopulationTypeDropdownOptions"
          class="profile-sales-filter-dropdown"
          @input="onSalesPopulationTypeChange"
        />
      </align>

      <div v-if="profileSalesForList.length" class="profile-recent-sales">
        <sales-list-item
          v-for="(item, i) in profileSalesForList"
          :key="item.id || i"
          :item="item"
          as="a"
          :href="buildExternalSaleOpenUrl(item)"
          target="_blank"
          rel="noopener noreferrer"
        >
          <align small-gap>
            <verified-icon v-if="item.cardId || item.memberVerified" />
          </align>
        </sales-list-item>
      </div>
    </section>

    <section class="graders-section">
      <div class="graders-section-header">
        <h4 class="short-text secondary-font">
          Population Report
        </h4>
        <pill-dropdown
          :value="populationReportValueDisplay"
          align="left"
          :options="[{ value: 'clValue', label: 'CL Value' }, { value: 'lastSold', label: 'Last sold' }]"
          class="population-report-value-dropdown"
          @input="populationReportValueDisplay = $event"
        />
      </div>

      <div v-for="graderItem in profile.graders" :key="graderItem.grader" class="grader-section">
        <div class="grader-section-header">
          <div class="grader-section-header-brand">
            <span v-if="getGraderLogoUrl(graderItem.grader)" class="profile-grader-logo-pill">
              <img
                class="profile-grader-logo-img"
                :src="getGraderLogoUrl(graderItem.grader)"
                :alt="graderItem.label"
              />
            </span>
            <h5 v-else class="short-text secondary-font">
              {{ graderItem.label }}
            </h5>
          </div>
          <pill-dropdown
            v-if="populationTypeOptions(graderItem).length > 1"
            :value="selectedPopulationTypeByGrader[graderItem.grader] || 'base'"
            align="left"
            :options="populationTypeOptions(graderItem)"
            class="grader-population-type-dropdown"
            @input="setPopulationType(graderItem.grader, $event)"
          />
        </div>

        <div class="grader-stats">
          <stat-item
            label="Total Population"
            :value="$filters.number(graderItem.totalPopulation)"
          />
          <stat-item
            label="Total Gems"
            :value="$filters.number(graderItem.totalGems)"
          />
          <stat-item
            label="Gem Rate"
            :value="$filters.percentage(graderItem.gemRate)"
          />
        </div>

        <div class="grades-grid">
          <div
            v-for="gradeItem in gradesForGrader(graderItem)"
            :key="gradeItem.grade"
            :class="['grade-item', { selected: isPopulationGradeSelected(graderItem, gradeItem) }, { 'no-sales': !gradeItem.price }]"
            @click="onPopulationReportGradeClick(graderItem, gradeItem)"
          >
            <span class="grade-label">{{ gradeItem.label }}</span>
            <div class="grade-metrics">
              <div
                class="grade-metric grade-metric--price"
              >
                <span class="grade-metric-label">{{ populationReportValueDisplay === 'clValue' ? 'CL Value' : 'Last sold' }}</span>
                <span class="grade-metric-value">
                  {{ populationReportValueDisplay === 'clValue'
                    ? (gradeItem.clValue != null ? $filters.currency(gradeItem.clValue) : '—')
                    : (gradeItem.price != null && gradeItem.price > 0 ? $filters.currency(gradeItem.price) : '—') }}
                </span>
              </div>
              <div
                class="grade-metric grade-metric--pop"
              >
                <span class="grade-metric-label">Pop</span>
                <span class="grade-metric-value">{{ $filters.number(gradeItem.value) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="index-container" @click="openIndex">
      <index-list-item :index="profile.index" />
    </div>
  </template>
</div>
